import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/react-query';
import { safeReturnPath } from '@/utils/safe-return-path';

function GoogleSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasHandledTokenRef = useRef(false);

  useEffect(() => {
    const handleGoogleSuccess = async () => {
      // Prevent duplicate execution
      if (hasHandledTokenRef.current) return;

      const token = searchParams.get('token');

      if (token) {
        hasHandledTokenRef.current = true;

        // Store the token - this is necessary as the backend returns the token here
        // The AuthContext will pick this up on next query
        localStorage.setItem('access_token', token);

        // Invalidate auth queries to force refetch with new token
        await queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });

        // Resume the flow that sent the user to login (validated ?next=), else
        // land on the dashboard. Lets agent OAuth consent survive Google login.
        const next = safeReturnPath(searchParams.get('next'));
        navigate(next ?? '/dashboard', { replace: true });
      } else {
        navigate('/auth/login?error=no_token', { replace: true });
      }
    };

    handleGoogleSuccess();
  }, [searchParams, navigate, queryClient]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
              Google Login Successful
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              You&apos;re being redirected to your dashboard...
            </p>
            <div className="mt-6 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GoogleSuccessPage;
